import { User } from '../types';
import { SIGNUP_ONBOARDING_ENABLED } from '../config/features';

/**
 * 組織に所属しているか。
 *
 * backend は user の直下 `organization_id` を返すが、経路によっては
 * `organization` を展開した形だけが redux / tokenManager に載ることがある。
 * 判定が箇所ごとにズレると「LoginPage は /signup/setup へ送るのにルートガードは
 * 通す」といった矛盾が起きるため、組織の有無の判定はこの関数に一本化する。
 */
export const hasOrganization = (user?: Partial<User> | null): boolean =>
  Boolean(user?.organization_id ?? user?.organization?.id);

/**
 * 管理者（Admin）としてユーザー画面を開いているか。
 *
 * 管理画面のログインは利用者と同じトークン置き場を使うので、管理者のままユーザー画面を
 * 開ける（`GET /api/user` は role='admin'・組織なしを返す）。管理者は組織を持たず、
 * 会社登録（`POST /api/onboarding/complete`）もできない（403）ので、組織なしでも
 * `/signup/setup` へは送らない。
 */
export const isAdminActor = (user?: Partial<User> | null): boolean => user?.role === 'admin';

/**
 * 認証成功直後（signup / login / マジックリンク）の着地先を決める。
 *
 * 組織未所属のユーザーを `/home` に着地させると、組織前提の API が一律 403 を
 * 返して「ログインできたのに何も操作できない」状態で詰む。`POST /api/auth/firebase`
 * は未知の Firebase ユーザーを組織なしで自動作成するため、Google ログインの
 * 新規ユーザーがこの状態に落ちる。したがって組織が無い場合は会社情報入力画面
 * `/signup/setup` へ送る。
 *
 * 呼び出し側の注意: authSlice の `isAuthenticated` は localStorage の
 * token/refresh_token だけで true に初期化されるので、リロード直後は
 * `isAuthenticated=true` かつ `user=null` という過渡状態が起こる。この間に
 * この関数を呼ぶと組織所属の既存ユーザーまで `/signup/setup` に送られるため、
 * 遷移の判定は `user` が入ってからにすること。
 *
 * `SIGNUP_ONBOARDING_ENABLED` が false の間、組織があるユーザーは
 * onboarding_completed を問わず `/home` に行く（フルオンボーディングは
 * 2026-07-18 から非表示）。
 */
export const getPostAuthDestination = (
  user?: Partial<User> | null,
): string => {
  // `!user` を明示するのは strict での narrowing のため（hasOrganization 経由だと
  // 下の user.onboarding_completed が null 可能性ありと判定される）。
  if (!user || !hasOrganization(user)) return '/signup/setup';
  if (!SIGNUP_ONBOARDING_ENABLED) return '/home';
  return user.onboarding_completed === true ? '/home' : '/signup/onboarding';
};

/**
 * 組織未所属ユーザーを素通りさせてはいけない画面かを判定する。
 *
 * `POST /api/auth/firebase` は未知の Firebase ユーザーを組織なしで自動作成する
 * ため、Google ログインの新規ユーザーは組織なしのまま画面に入りうる。その状態で
 * /home に着地すると `GET /api/home/summary` と `GET /api/organizations` が 404
 * を返す。401 ではないので axios の interceptor は動かず、何も出ない画面がその
 * まま残る（本番で観測）。ログイン直後の `getPostAuthDestination` だけでは
 * リロード等でこの状態に戻れてしまうので、ルートガード側でも同じ判定をする。
 *
 * 判定は `user` が読めているときだけ行う。`isAuthenticated=true` かつ
 * `user=null` の過渡状態で true を返すと、組織所属の既存ユーザーまで
 * `/signup/setup` に飛ばしてしまう。
 */
const SETUP_EXEMPT_PREFIXES = [
  '/signup', // /signup/setup 自身と登録フロー
  '/invite', // 招待受諾で組織に入る導線
  '/auth', // マジックリンク等の認証コールバック
  '/logout',
  '/login',
  '/admin', // 別系統の認証（AdminRoute）
];

export const shouldRedirectToOrganizationSetup = (
  user: Partial<User> | null | undefined,
  pathname: string,
): boolean => {
  if (!user) return false;
  if (hasOrganization(user) || isAdminActor(user)) return false;

  const path = (pathname || '').toLowerCase();
  return !SETUP_EXEMPT_PREFIXES.some((prefix) => path.startsWith(prefix));
};

export default getPostAuthDestination;
